import _ from 'underscore';
import React, { useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { BingAdsSwitchModalStrings, SwitchExpertModal } from '@bingads-webui-react/switch-expert-modal';

import { getSwitchToExpertMenuStyles } from './styles/switch-to-expert-menu-styles';
import { convertSmartToExpertMode } from './convert-smart-to-expert-mode';

const SwitchToExpertInternal = ({
  account,
  serverContext,
  settingsContext,
  classes,
  closeDropdown,
  onCancelExpertMode,
  onChangeToExpertMode,
  shouldReload,
}) => {
  const i18n = useContext(I18nContext);
  const {
    CurrentCustomer: {
      Id: customerId,
    },
    CurrentUser: {
      Id: userId,
    },
  } = serverContext;

  const {
    Id: accountId,
    CurrencyCode,
    TimeZone,
  } = account;

  const [isOpen, setIsOpen] = useState(false);

  // if popup is open, memoize call so we de-dupe all outgoing calls
  const convertAccount = useMemo(
    () => _.memoize(convertSmartToExpertMode, args => isOpen && args.accountId),
    [isOpen]
  );
  const handleConfirm = () => convertAccount({
    ccuiEndpoint: settingsContext.Urls.ClientCenterBaseUrl,
    accountId,
    customerId,
    userId,
  })
    .then((result) => {
      onChangeToExpertMode(result);
      return result;
    });

  const handleClose = () => onCancelExpertMode()
    .then((result) => {
      setIsOpen(false);
      return result;
    });

  const onClick = () => {
    closeDropdown();
    setIsOpen(true);
  };

  const legacyI18n = useMemo(() => buildLegacyI18n(
    i18n,
    [BingAdsSwitchModalStrings],
    {
      currency: CurrencyCode,
      timeZone: TimeZone,
    }
  ), [CurrencyCode, TimeZone, i18n]);

  return (
    <div className="switch-to-expert-option-container">
      <Button
        className={classNames([classes.switchButton, 'switch-to-expert-button'])}
        onClick={onClick}
        data-analyticsid="switch-expert-modal_show"
      >
        <span className="switch-to-expert-text">
          {i18n.getString(_TL_('Switch to Expert Mode'))}
        </span>
      </Button>
      <SwitchExpertModal
        isOpen={isOpen}
        isFluentNewUI
        shouldReload={shouldReload}
        onClose={handleClose}
        onSuccess={handleConfirm}
        i18n={legacyI18n}
        context={{
          accountId,
          customerId,
          userId,
        }}
      />
    </div>
  );
};

SwitchToExpertInternal.propTypes = {
  account: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    AccountNumber: PropTypes.string.isRequired,
    ParentCustomerId: PropTypes.number.isRequired,
    AccountMode: PropTypes.number.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  settingsContext: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  closeDropdown: PropTypes.func.isRequired,
  onChangeToExpertMode: PropTypes.func,
  onCancelExpertMode: PropTypes.func,
  shouldReload: PropTypes.bool.isRequired,
};

SwitchToExpertInternal.defaultProps = {
  onChangeToExpertMode: result => Promise.resolve(result),
  onCancelExpertMode: result => Promise.resolve(result),
};

export const SwitchToExpertMenu = withDefaultStyles(
  SwitchToExpertInternal,
  getSwitchToExpertMenuStyles
);
